import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/investigation-dashboard',
      icon: 'LayoutDashboard',
      description: 'Case management and investigation overview'
    },
    {
      label: 'Transaction Analysis',
      path: '/transaction-analysis',
      icon: 'Search',
      description: 'Detailed blockchain transaction examination'
    },
    {
      label: 'Risk Assessment',
      path: '/address-risk-assessment',
      icon: 'Shield',
      description: 'Address and wallet risk evaluation'
    },
    {
      label: 'Fund Flow Mapping',
      path: '/fund-flow-mapping',
      icon: 'GitBranch',
      description: 'Complex transaction flow visualization'
    },
    {
      label: 'Forensic Reports',
      path: '/forensic-report-generator',
      icon: 'FileText',
      description: 'Professional investigation documentation'
    }
  ];

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const handleNavigation = (path) => {
    window.location.href = path;
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-1000 bg-surface border-b border-border shadow-forensic-card">
      <div className="flex items-center justify-between h-16 px-6">
        {/* Logo Section */}
        <div className="flex items-center">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg">
              <Icon name="Shield" size={20} color="white" strokeWidth={2.5} />
            </div>
            <div className="flex flex-col">
              <h1 className="text-lg font-semibold text-primary tracking-tight">
                CryptoTrace
              </h1>
              <span className="text-xs text-text-secondary font-medium">
                Forensic Intelligence
              </span>
            </div>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-1">
          {navigationItems?.map((item) => (
            <button
              key={item?.path}
              onClick={() => handleNavigation(item?.path)}
              className={`
                flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-forensic
                ${isActivePath(item?.path)
                  ? 'bg-primary text-primary-foreground shadow-forensic-card'
                  : 'text-text-secondary hover:text-text-primary hover:bg-muted'
                }
              `}
              title={item?.description}
            >
              <Icon 
                name={item?.icon} 
                size={16} 
                color="currentColor" 
                strokeWidth={2}
              />
              <span>{item?.label}</span>
            </button>
          ))}
        </nav>

        {/* Mobile Menu Button */}
        <div className="lg:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMenu}
            className="text-text-secondary hover:text-text-primary"
          >
            <Icon 
              name={isMenuOpen ? "X" : "Menu"} 
              size={20} 
              color="currentColor" 
            />
          </Button>
        </div>

        {/* User Actions - Desktop */}
        <div className="hidden lg:flex items-center space-x-3">
          <Button
            variant="ghost"
            size="sm"
            className="text-text-secondary hover:text-text-primary"
          >
            <Icon name="Bell" size={16} color="currentColor" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-text-secondary hover:text-text-primary"
          >
            <Icon name="Settings" size={16} color="currentColor" />
          </Button>
          <div className="w-px h-6 bg-border mx-2" />
          <Button
            variant="outline"
            size="sm"
            className="text-text-primary border-border"
          >
            <Icon name="User" size={16} color="currentColor" className="mr-2" />
            Agent Smith
          </Button>
        </div>
      </div>
      {/* Mobile Navigation Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-surface border-t border-border shadow-forensic-modal">
          <nav className="px-6 py-4 space-y-2">
            {navigationItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`
                  flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-sm font-medium transition-forensic
                  ${isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-text-secondary hover:text-text-primary hover:bg-muted'
                  }
                `}
              >
                <Icon 
                  name={item?.icon} 
                  size={18} 
                  color="currentColor" 
                  strokeWidth={2}
                />
                <div className="flex flex-col items-start">
                  <span>{item?.label}</span>
                  <span className="text-xs opacity-75">{item?.description}</span>
                </div>
              </button>
            ))}
            
            {/* Mobile User Actions */}
            <div className="pt-4 mt-4 border-t border-border">
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-text-secondary hover:text-text-primary"
                >
                  <Icon name="Bell" size={16} color="currentColor" className="mr-2" />
                  Notifications
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-text-secondary hover:text-text-primary"
                >
                  <Icon name="Settings" size={16} color="currentColor" className="mr-2" />
                  Settings
                </Button>
              </div>
              <div className="mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  className="text-text-primary border-border justify-start"
                >
                  <Icon name="User" size={16} color="currentColor" className="mr-2" />
                  Agent Smith
                </Button>
              </div>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;